import { LoadingState } from '../ui';
// src/pages/MyCompetitions.jsx

import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import { useNavigate } from "react-router-dom";
import { getCompetitionTypeDisplay } from '../lib/competitionUtils';
import EnrolledCompetitionCard from '../components/EnrolledCompetitionCard';
import { reminderSystem } from '../lib/reminderNotifications';
import { Button as FcButton, Card as FcCard, Select as FcSelect } from '../ui';

export default function MyCompetitions({ session, userRole, username }) {
  const navigate = useNavigate();
  // Logout handler
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const getCompetitionRoute = (competitionId, competitionType) =>
    competitionType === 'world_stage'
      ? `/world-stage/${competitionId}`
      : `/competition/${competitionId}`;
  const [selections, setSelections] = useState([]);
  const [filteredSelections, setFilteredSelections] = useState([]);
  const [teamSelections, setTeamSelections] = useState([]);
  const [matchdays, setMatchdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [leagueFilter, setLeagueFilter] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // User Status
  const [competitionStatusFilter, setCompetitionStatusFilter] = useState("active");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const cardsPerPage = 9;

  const formatType = (type) =>
    type?.charAt(0).toUpperCase() + type?.slice(1);

  useEffect(() => {
    const fetchData = async () => {
      // 1. Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        setLoading(false);
        return;
      }

      // Run all data fetching queries in parallel
      const [userSelectionsResult, teamSelectionsResult, matchdaysResult] = await Promise.allSettled([
        // Fetch user selections (joined with competitions) - exclude Scout competitions
        supabase
          .from("user_selections")
          .select("competition_id, league, season, match_day, status, competitions!inner(id, name, current_match_day, type, status, is_18_plus, visibility, player_count, is_multi_league)")
          .eq("user_id", user.id)
          .neq("competitions.type", "scout"),
        // Fetch team selections
        supabase
          .from("team_selections")
          .select("competition_id, selected_team, match_day")
          .eq("user_id", user.id),
        // Fetch all matchdays
        supabase
          .from("matchdays_view")
          .select("*")
      ]);

      // Process results
      let compData = userSelectionsResult.status === 'fulfilled' ? userSelectionsResult.value.data || [] : [];
      const teamData = teamSelectionsResult.status === 'fulfilled' ? teamSelectionsResult.value.data || [] : [];
      const matchdayData = matchdaysResult.status === 'fulfilled' ? matchdaysResult.value.data || [] : [];

      // Fetch league logos from api_football_leagues
      const leagueNames = [...new Set(compData.map(c => c.league).filter(Boolean))];
      const leagueLogoMap = {};
      if (leagueNames.length > 0) {
        const { data: leaguesData } = await supabase
          .from("api_football_leagues")
          .select("league_name, logo, flag")
          .in("league_name", leagueNames);
        
        if (leaguesData) {
          for (const league of leaguesData) {
            leagueLogoMap[league.league_name] = {
              logo: league.logo,
              flag: league.flag
            };
          }
        }
      }

      // For world_stage competitions, derive stage from the earliest incomplete fixture
      // per (league, season) pair.
      const worldStageSelections = compData.filter(
        (sel) => sel.competitions?.type === "world_stage" && sel.league && sel.season
      );
      const worldStageLeagues = [...new Set(worldStageSelections.map((s) => s.league))];
      const worldStageSeasons = [...new Set(worldStageSelections.map((s) => s.season))];
      const worldStageStageMap = {};

      if (worldStageLeagues.length > 0 && worldStageSeasons.length > 0) {
        const { data: stageFixtures } = await supabase
          .from("fixtures")
          .select("league, season, stage, kickoff_time, match_day, created_at")
          .in("league", worldStageLeagues)
          .in("season", worldStageSeasons)
          .eq("status", "incomplete");

        const fixturesByKey = {};
        for (const fixture of stageFixtures || []) {
          const key = `${fixture.league}|||${fixture.season}`;
          if (!fixturesByKey[key]) fixturesByKey[key] = [];
          fixturesByKey[key].push(fixture);
        }

        for (const [key, fixtures] of Object.entries(fixturesByKey)) {
          fixtures.sort((a, b) => {
            const aKickoff = a.kickoff_time ? new Date(a.kickoff_time).getTime() : Number.MAX_SAFE_INTEGER;
            const bKickoff = b.kickoff_time ? new Date(b.kickoff_time).getTime() : Number.MAX_SAFE_INTEGER;
            if (aKickoff !== bKickoff) return aKickoff - bKickoff;

            const aMatchDay = Number.isFinite(a.match_day) ? a.match_day : Number.MAX_SAFE_INTEGER;
            const bMatchDay = Number.isFinite(b.match_day) ? b.match_day : Number.MAX_SAFE_INTEGER;
            if (aMatchDay !== bMatchDay) return aMatchDay - bMatchDay;

            const aCreated = a.created_at ? new Date(a.created_at).getTime() : Number.MAX_SAFE_INTEGER;
            const bCreated = b.created_at ? new Date(b.created_at).getTime() : Number.MAX_SAFE_INTEGER;
            return aCreated - bCreated;
          });

          const firstFixture = fixtures[0];
          worldStageStageMap[key] = (firstFixture?.stage || "").trim() || null;
        }
      }

      // Enrich selections with league logos
      compData = compData.map(sel => {
        const leagueData = leagueLogoMap[sel.league] || {};
        const stageKey = `${sel.league}|||${sel.season}`;
        return {
          ...sel,
          competitions: {
            ...sel.competitions,
            league_logo_url: leagueData.logo,
            league_flag_url: leagueData.flag,
            current_stage:
              sel.competitions?.type === "world_stage"
                ? worldStageStageMap[stageKey] || null
                : null,
          }
        };
      });

      setSelections(compData);
      setFilteredSelections(compData);
      setTeamSelections(teamData);
      setMatchdays(matchdayData);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Helper: lock team selection 1 hour before first kickoff of matchday
  const isSelectionLocked = (matchDay, league, season) => {
    // Find the kickoff time for the given matchday, league, and season
    const match = matchdays.find(
      (m) =>
        m.league === league &&
        m.season === season &&
        m.match_day === matchDay
    );
    if (!match || !match.first_kickoff_time) return false;
    const kickoff = new Date(match.first_kickoff_time);
    const now = new Date();
    const lockTime = new Date(kickoff);
    lockTime.setHours(lockTime.getHours() - 1);
    return now > lockTime;
  };

  // Helper: find selected team for a competition and matchday
  const findSelectedTeam = (compId, matchDay) => {
    const selection = teamSelections.find(
      (team) =>
        String(team.competition_id) === String(compId) &&
        team.match_day === matchDay
    );
    return selection ? selection.selected_team : null;
  };

  // Helper: reset all filters
  const resetFilters = () => {
    setTypeFilter("");
    setLeagueFilter("");
    setSeasonFilter("");
    setStatusFilter("");
    setCompetitionStatusFilter("active");
  };

  // Filtered selections logic
  useEffect(() => {
    let filtered = selections;
    if (typeFilter) filtered = filtered.filter((s) => s.competitions?.type === typeFilter);
    if (leagueFilter) filtered = filtered.filter((s) => s.league === leagueFilter);
    if (seasonFilter) filtered = filtered.filter((s) => s.season === seasonFilter);
    if (statusFilter) filtered = filtered.filter((s) => s.status === statusFilter);
    if (competitionStatusFilter) filtered = filtered.filter((s) => s.competitions?.status === competitionStatusFilter);
    setFilteredSelections(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  }, [typeFilter, leagueFilter, seasonFilter, statusFilter, competitionStatusFilter, selections]);

  const activeSelections = selections.filter(
    (s) => s.competitions?.status === "active"
  );

  const leagueOptions = useMemo(() => {
    return [...new Set((selections || []).map((s) => s.league).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [selections]);

  const seasonOptions = useMemo(() => {
    const seasonSource = leagueFilter
      ? (selections || []).filter((s) => s.league === leagueFilter)
      : (selections || []);
    return [...new Set(seasonSource.map((s) => s.season).filter(Boolean))]
      .sort((a, b) => b.localeCompare(a));
  }, [selections, leagueFilter]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (typeFilter) {
      chips.push({ key: "type", label: `Type: ${getCompetitionTypeDisplay(typeFilter)}`, onClear: () => setTypeFilter("") });
    }
    if (leagueFilter) {
      chips.push({ key: "league", label: `League: ${leagueFilter}`, onClear: () => setLeagueFilter("") });
    }
    if (seasonFilter) {
      chips.push({ key: "season", label: `Season: ${seasonFilter}`, onClear: () => setSeasonFilter("") });
    }
    if (statusFilter) {
      const statusLabel = statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);
      chips.push({ key: "user-status", label: `Your Status: ${statusLabel}`, onClear: () => setStatusFilter("") });
    }
    if (competitionStatusFilter) {
      const competitionLabel = competitionStatusFilter.charAt(0).toUpperCase() + competitionStatusFilter.slice(1);
      chips.push({ key: "competition-status", label: `Competition: ${competitionLabel}`, onClear: () => setCompetitionStatusFilter("") });
    }
    return chips;
  }, [typeFilter, leagueFilter, seasonFilter, statusFilter, competitionStatusFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredSelections.length / cardsPerPage);
  const paginatedSelections = filteredSelections.slice(
    (currentPage - 1) * cardsPerPage,
    currentPage * cardsPerPage
  );

  return (
    <div className="min-h-screen bg-surface-canvas">
      {/* Fixed green banner at the top, only on mobile/tablet */}
      <div className="fixed top-0 left-0 w-full z-40 bg-ui-green text-white py-3 font-bold text-lg shadow-md flex items-center md:hidden">
        <span className="ml-6">Footy Coach</span>
      </div>
      {/* Offset for fixed banner */}
      <div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-20 md:pt-8 px-2 sm:px-4 md:p-8 pb-28 fc-workspace">
          <h2 className="text-2xl font-bold mb-6 text-brand-text">My Competitions ({filteredSelections.length})</h2>
          <FcCard as="div" padding="standard" className="bg-surface-card border border-ui-border rounded-xl shadow-sm p-4 sm:p-5 mb-6">
            <div className="md:hidden flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wide text-content-secondary">Filter options</span>
              <FcButton variant="tertiary"
                type="button"
                onClick={() => setShowMobileFilters((prev) => !prev)}
                className="hover:underline"
              >
                {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
              </FcButton>
            </div>

            <div className={`${showMobileFilters ? 'grid' : 'hidden'} md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3`}>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">Type</label>
                <FcSelect aria-label="Type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="border"
                >
                  <option value="">All Types</option>
                  <option value="knockout">Knockout</option>
                  <option value="season">Season</option>
                  <option value="world_stage">World Stage</option>
                </FcSelect>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">League</label>
                <FcSelect aria-label="League"
                  value={leagueFilter}
                  onChange={(e) => setLeagueFilter(e.target.value)}
                  className="border"
                >
                  <option value="">All Leagues</option>
                  {leagueOptions.map((league) => (
                    <option key={league} value={league}>{league}</option>
                  ))}
                </FcSelect>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">Season</label>
                <FcSelect aria-label="Season"
                  value={seasonFilter}
                  onChange={(e) => setSeasonFilter(e.target.value)}
                  className="border"
                >
                  <option value="">All Seasons</option>
                  {seasonOptions.map((season) => (
                    <option key={season} value={season}>{season}</option>
                  ))}
                </FcSelect>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">Your Status</label>
                <FcSelect
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border"
                  aria-label="Your Status"
                  title="Your Competition Status"
                >
                  <option value="">All Your Statuses</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="eliminated">Eliminated</option>
                </FcSelect>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">Competition</label>
                <FcSelect
                  value={competitionStatusFilter}
                  onChange={(e) => setCompetitionStatusFilter(e.target.value)}
                  className="border"
                  aria-label="Competition"
                  title="Competition Status"
                >
                  <option value="">All Competition Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </FcSelect>
              </div>
            </div>

            <div className={`${showMobileFilters ? 'flex' : 'hidden'} md:flex mt-3 justify-end`}>
              <FcButton variant="secondary" size="compact" type="submit"
                onClick={resetFilters}
                className="disabled:cursor-not-allowed"
                disabled={activeFilterChips.length === 0}
              >
                Reset Filters
              </FcButton>
            </div>
          </FcCard>

          {activeFilterChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-5 -mt-2">
              {activeFilterChips.map((chip) => (
                <FcButton variant="secondary" type="submit"
                  key={chip.key}
                  onClick={chip.onClear}
                  className="inline-flex items-center gap-2 border"
                >
                  <span>{chip.label}</span>
                  <span aria-hidden="true">×</span>
                </FcButton>
              ))}
              <FcButton variant="tertiary" type="submit"
                onClick={resetFilters}
                className="hover:underline"
              >
                Clear all
              </FcButton>
            </div>
          )}

          {loading ? (
            <LoadingState label="Loading competitions..." />
          ) : activeSelections.length === 0 ? (
            <div className="p-6 text-center">
              <p className="mb-4 text-brand-text-muted">
                You have no active competitions. Go to the competitions section now to
                keep up with the game.
              </p>
              <Link to="/competitions">
                <FcButton variant="primary" type="submit" className="">
                  View Competitions
                </FcButton>
              </Link>
            </div>
          ) : filteredSelections.length === 0 ? (
            <FcCard as="div" padding="standard" className="border border-ui-border bg-surface-card rounded-lg p-6 text-center">
              <p className="text-brand-text-muted mb-3">
                You have not joined any competitions matching these filters.
              </p>
              <FcButton variant="primary" type="submit"
                onClick={resetFilters}
                className=""
              >
                Clear Filters
              </FcButton>
            </FcCard>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {paginatedSelections.map((sel, index) => {
                  const isEliminated =
                    sel.status === "eliminated" && sel.competitions?.type === "knockout";
                  const selectedTeam = findSelectedTeam(
                    sel.competition_id,
                    sel.competitions?.current_match_day
                  );
                  const isLocked = isSelectionLocked(
                    sel.competitions?.current_match_day,
                    sel.league,
                    sel.season
                  );

                  return (
                    <EnrolledCompetitionCard
                      key={index + (currentPage - 1) * cardsPerPage}
                      sel={sel}
                      selectedTeam={selectedTeam}
                      isSelectionLocked={isLocked}
                      isEliminated={isEliminated}
                      getRoute={getCompetitionRoute}
                    />
                  );
                })}
              </div>
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center mt-6 mb-2 gap-4">
                  <FcButton variant="secondary" type="submit"
                    className={`border ${currentPage === 1 ? "cursor-not-allowed" : ""}`}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </FcButton>
                  <span className="font-semibold text-brand-text text-base">
                    Page {currentPage} of {totalPages}
                  </span>
                  <FcButton variant="secondary" type="submit"
                    className={`border ${currentPage === totalPages ? "cursor-not-allowed" : ""}`}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </FcButton>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
